// The one statement of "which contest is mine". SUR-R39 promises a fan who
// joins a pool mid-contest becomes a member and plays the next contest, and
// SUR-R1 and SUR-R3 let a pool run several contests in one season as long as
// their dates do not overlap. So "the contest this fan is in" is not "the one
// that happens to be running": a fan seeded into the pool's next contest is in
// that one while the first still runs.
//
// This is eligibility-shaped, so it belongs on the server and ships as a field
// (server-resolves-client-renders.mdc). apps/app used to scan the contest list
// for status === "active" itself, which is the defect ISSUE-777 records.
//
// No clock. Ordering is a plain string compare on the YYYY-MM-DD calendar day
// contests carry, which is chronological for that format and holds no
// timezone. Nothing here reads today's date, so nothing here can drift across
// a day edge or a daylight-saving boundary.
//
// Lives under src/picks/ (not src/contests/): the engine layout guard ratifies
// nine src modules and contests is not one of them. It sits beside
// resolvePickEligibility because both answer a question the pick screen asks.

export type typeViewerContestStatus =
  "upcoming" | "active" | "pendingOutcome" | "confirmed" | "cancelled";

/**
 * The stored status a commissioner sets when calling a contest off.
 *
 * Exported as a value, not just a union member, so a consumer outside this
 * package can name it without writing the British spelling itself. The spelling
 * is the persisted contract: services/contest-api writes it to MongoDB and the
 * generated SDK types carry it, so renaming it is a data migration rather than
 * a spelling fix. This package is the one place the American English guard
 * exempts it by a reviewed allowlist entry, and re-exporting it here keeps that
 * exemption from having to spread to product copy that the guard scans in full.
 */
export const VIEWER_CONTEST_STATUS_CALLED_OFF: typeViewerContestStatus =
  "cancelled";

export type typeViewerContestCandidate = {
  contestId: string;
  status: typeViewerContestStatus;
  // A calendar day, YYYY-MM-DD, never an instant.
  startDate: string;
};

export type typeViewerContestState =
  "playing" | "waiting" | "spectating" | "none";

export type typeResolveViewerContestInput = {
  contests: typeViewerContestCandidate[];
  enteredContestIds: string[];
};

export type typeResolveViewerContestResult = {
  contestId: string | null;
  state: typeViewerContestState;
};

// SUR-R61: a contest ends when the Commissioner confirms it, so pendingOutcome
// is still being played. confirmed and cancelled are never a fan's current
// contest, in any branch below.
function isRunning(contest: typeViewerContestCandidate): boolean {
  return contest.status === "active" || contest.status === "pendingOutcome";
}

// Ties break on contestId so the answer is deterministic. Constraint C5
// already forbids two contests in one pool sharing a calendar day, so a tie
// means bad data rather than a real case, and a stable answer beats an
// arbitrary one.
function byStartAsc(
  a: typeViewerContestCandidate,
  b: typeViewerContestCandidate,
): number {
  if (a.startDate !== b.startDate) return a.startDate < b.startDate ? -1 : 1;
  if (a.contestId !== b.contestId) return a.contestId < b.contestId ? -1 : 1;
  return 0;
}

function byStartDesc(
  a: typeViewerContestCandidate,
  b: typeViewerContestCandidate,
): number {
  if (a.startDate !== b.startDate) return a.startDate < b.startDate ? 1 : -1;
  if (a.contestId !== b.contestId) return a.contestId < b.contestId ? -1 : 1;
  return 0;
}

// A linear scan for the best match, not a sort. Two reasons, both real:
// pickPriorityIsSingleSource.test.ts keeps resolvePickPriority.ts the only
// file in this module allowed to sort at all, and a scan cannot reorder the
// caller's array by accident.
function firstMatch(
  contests: typeViewerContestCandidate[],
  keep: (contest: typeViewerContestCandidate) => boolean,
  order: (
    a: typeViewerContestCandidate,
    b: typeViewerContestCandidate,
  ) => number,
): typeViewerContestCandidate | null {
  let best: typeViewerContestCandidate | null = null;
  for (const contest of contests) {
    if (!keep(contest)) continue;
    if (best === null || order(contest, best) < 0) best = contest;
  }
  return best;
}

export function resolveViewerContest(
  input: typeResolveViewerContestInput,
): typeResolveViewerContestResult {
  const entered = new Set(input.enteredContestIds);

  // Playing beats waiting. A fan who holds an entry in the contest that is
  // running is picking in it right now, whatever else the pool has scheduled.
  const playing = firstMatch(
    input.contests,
    (contest) => entered.has(contest.contestId) && isRunning(contest),
    byStartDesc,
  );
  if (playing) return { contestId: playing.contestId, state: "playing" };

  // The fan this card is about: seeded into the pool's next contest, holding
  // nothing in the one that is running. Earliest start wins, because that is
  // the one they play next.
  //
  // How often does this happen today? Rarely, and the honest answer belongs
  // here. The open-round sweep seeds a contest's entries and flips it from
  // upcoming to active under the same condition, in the same tick. See
  // jobs/openRound/index.ts. So the normal path never parks a fan with an
  // entry in a contest still marked upcoming. Two things still produce it.
  // First, seedContestEntries can create some entries and then throw on a
  // later member, which skips the status flip until the next sweep. Second,
  // the seed and the flip are two writes with no transaction around them.
  // Both are short lived, and both are exactly when a fan must not be shown a
  // contest they hold nothing in.
  //
  // This branch is also what contest succession will need. Q-777-A ruled the
  // Commissioner starts the next contest by hand, so no job seeds a fan ahead
  // of a contest yet. When one does, this is already the right answer.
  // A fan who joined mid-contest and holds no entry anywhere does not reach
  // here at all. They fall to spectating, and the pick screen tells them a
  // Commissioner starts the next contest. That is ISSUE-777 D4.
  const waiting = firstMatch(
    input.contests,
    (contest) =>
      entered.has(contest.contestId) && contest.status === "upcoming",
    byStartAsc,
  );
  if (waiting) return { contestId: waiting.contestId, state: "waiting" };

  // No entry anywhere. Show them the pool's running contest, or its next one
  // when nothing is running, so the screen still has something to say.
  const running = firstMatch(input.contests, isRunning, byStartDesc);
  if (running) return { contestId: running.contestId, state: "spectating" };

  const upcoming = firstMatch(
    input.contests,
    (contest) => contest.status === "upcoming",
    byStartAsc,
  );
  if (upcoming) return { contestId: upcoming.contestId, state: "spectating" };

  return { contestId: null, state: "none" };
}
